<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { passwordResetApi } from '@/services/api/passwordReset';
import { useNotifier } from '@/composables/notifier';
import { usePasswordStrength } from '@/composables/usePasswordStrength';

const route = useRoute();
const router = useRouter();
const notify = useNotifier();

const password = ref('');
const confirmPassword = ref('');
const submitting = ref(false);

const passwordStrength = usePasswordStrength(password);
const valid = computed(
  () =>
    password.value.length > 0 &&
    password.value === confirmPassword.value,
);

function tokenParam(): string {
  const raw = route.params.token;
  return Array.isArray(raw) ? (raw[0] ?? '') : raw ?? '';
}

async function submit(): Promise<void> {
  if (!valid.value || submitting.value) return;
  submitting.value = true;
  try {
    await passwordResetApi.changePassword(tokenParam(), password.value);
    notify.info('Your password has been changed!');
    await router.replace({ name: 'main' });
  } catch {
    notify.error('Unable to change password. The link may have expired.');
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <form class="reset" novalidate @submit.prevent="submit">
    <h1 class="reset__title">Change Password</h1>

    <label class="reset__field" for="reset-password">
      <input
        id="reset-password"
        v-model="password"
        type="password"
        name="password"
        placeholder="Password"
        required
      />
      <meter min="1" max="4" :value="passwordStrength" low="2" high="3" optimum="4" />
    </label>

    <label class="reset__field" for="reset-confirm">
      <input
        id="reset-confirm"
        v-model="confirmPassword"
        type="password"
        name="confirm_password"
        placeholder="Confirm Password"
        required
      />
    </label>

    <button type="submit" class="reset__submit" :disabled="!valid || submitting">
      {{ submitting ? 'Saving…' : 'Change Password' }}
    </button>
  </form>
</template>

<style scoped>
.reset {
  max-width: 24rem;
  margin: 4rem auto;
  padding: 2rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.reset__title {
  margin: 0;
  font-size: 1.25rem;
  text-align: center;
}
.reset__field input {
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}
.reset__field meter {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
}
.reset__submit {
  padding: 0.625rem 1rem;
  background: #2563eb;
  color: #fff;
  border: 0;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
}
.reset__submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
